import { inject, Injectable, OnDestroy } from '@angular/core';
import {
  catchError,
  EMPTY,
  finalize,
  map,
  Observable,
  of,
  startWith,
  Subject,
  Subscription,
  switchMap,
  tap,
} from 'rxjs';
import { SessionService } from 'src/app/auth/session.service';
import { completeSubject, unsubscribeAll } from 'src/app/common/app.helpers';
import { loadingHttpContext } from 'src/app/common/interceptors/tokens';
import { Session } from 'src/app/interfaces';
import { SelectorOption } from 'src/app/interfaces/form-and-field.interface';
import {
  RequestSearchParams,
  SearchRequest,
  SearchResultResponse,
  SearchState,
} from 'src/app/interfaces/search.interface';
import { DefaultService as RestApiService } from 'src/app/swagger-api/api/default.service';
import { SearchResponse } from 'src/app/swagger-api/model/searchResponse';
import { SearchCategories, SearchStrategyTypes } from './search.enum';
import { authenticatedSearchOptions, unauthenticatedSearchOptions } from './search.options';
import {
  DefaultSearch,
  EntityNameSearch,
  InviteByNamesSearch,
  InviteUsersSearch,
  MapSearch,
  SearchStrategy,
} from './search.strategy';

@Injectable({ providedIn: 'root' })
export class SearchService implements OnDestroy {
  private searchRequest$: Subject<SearchRequest> = new Subject();
  private searchOptionsChanges$: Subject<SelectorOption[]> = new Subject();
  private isLoading: Subject<boolean> = new Subject();

  private searchStrategy: SearchStrategy;
  private searchState: SearchState = this.initialSearchState();

  private subscriptions: Subscription[] = [];

  private apiService = inject(RestApiService);
  private sessionService = inject(SessionService);

  public constructor() {
    this.setStrategy();
  }

  public ngOnDestroy(): void {
    completeSubject(this.searchRequest$);
    completeSubject(this.searchOptionsChanges$);
    unsubscribeAll(this.subscriptions);
  }

  public get searchOptionsChanged(): Observable<SelectorOption[]> {
    return !this.sessionService.token
      ? of(unauthenticatedSearchOptions)
      : this.sessionService.sessionChanges.pipe(
          map((session: Session) => {
            return session.token ? authenticatedSearchOptions : unauthenticatedSearchOptions;
          }),
        );
  }

  public get isLoading$(): Observable<boolean> {
    return this.isLoading.pipe(startWith(false));
  }

  /**
   * @description Sets the search strategy depend on the passed type.
   * @param { SearchStrategyTypes } type
   */
  public setStrategy(type: SearchStrategyTypes = SearchStrategyTypes.DEFAULT): void {
    const mapper: { [key in SearchStrategyTypes]: SearchStrategy } = {
      [SearchStrategyTypes.DEFAULT]: new DefaultSearch(),
      [SearchStrategyTypes.INVITE]: new InviteUsersSearch(this.sessionService),
      [SearchStrategyTypes.INVITE_NAMES]: new InviteByNamesSearch(this.sessionService),
      [SearchStrategyTypes.MAP]: new MapSearch(),
      [SearchStrategyTypes.ENTITY_NAME]: new EntityNameSearch(),
    };

    this.searchStrategy = mapper[type];
  }

  public initialSearchState(): SearchState {
    return {
      params: { queryText: '', itemKind: SearchCategories.ALL_CATEGORIES },
      pagination: { currentPage: 1, totalItemsCount: 0 },
    };
  }

  /**
   * @description Fetches markers data with specified search params.
   * @param { RequestSearchParams } requestSearchParams
   * @returns { Observable<SearchResultResponse> }
   */
  public getSearchResults(requestSearchParams: RequestSearchParams): Observable<SearchResultResponse> {
    let stringifiedRequest = '';

    if (typeof requestSearchParams.state.sortParams !== 'undefined') {
      this.searchStrategy.sortParams = requestSearchParams.state.sortParams;
    }

    stringifiedRequest = this.searchStrategy.formSearchParams({
      previousState: this.searchState,
      currentState: requestSearchParams.state,
      applyLocation: requestSearchParams.applyLocationAttrs,
      locationLiteral: requestSearchParams.locationLiteral,
    });

    this.searchRequest$.next(Object.assign(requestSearchParams.state, { stringifiedRequest }));

    this.searchState = requestSearchParams.state;

    return this.searchResultsChanges;
  }

  /**
   * @description Fires on searchRequest$ changes, then fetches data of requested search
   *              items and processed it.
   */
  public get searchResultsChanges(): Observable<SearchResponse> {
    return this.searchRequest$.asObservable().pipe(
      tap(() => this.isLoading.next(true)),
      switchMap((request: SearchRequest) => {
        return this.getSearchResponse(request);
      }),
    );
  }

  private getSearchResponse(request: SearchRequest): Observable<SearchResponse> {
    return this.apiService.restSearchResultsGet(request.stringifiedRequest, loadingHttpContext()).pipe(
      finalize(() => this.isLoading.next(false)),
      map((response: SearchResponse) => this.searchStrategy.getProcessedResponse(response, request)),
      catchError(() => EMPTY),
    );
  }
}
